import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { CompletionModal } from './CompletionModal';
import { SettingsModal } from './SettingsModal';
import { LeaderboardModal } from './LeaderboardModal';
import { DailyChallengeModal, DailyCompletionModal } from './DailyChallengeModal';
import { CreateChallengeModal } from './CreateChallengeModal/index';
import { AvailableChallengesModal } from './AvailableChallengesModal';
import { ChallengeInfoModal } from './ChallengeInfoModal';
import { ChallengeCompletionModal } from './ChallengeCompletionModal';
import { HistoryModal } from './HistoryModal';
import { HelpModal } from './HelpModal';
import { SecretMenuModal } from './SecretMenuModal';
import { CheatsPortalModal } from './CheatsPortalModal';
import { Button } from '../ui/button';
import { X } from 'lucide-react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../ui/alert-dialog';
import { topModalCoordinator } from '../../lib/topModalCoordinator';
import { DIFFICULTIES } from '../../lib/constants';

/**
 * ModalCoordinator
 *
 * Centralises rendering of every modal/dialog used by the game, along with
 * all the state that is purely about modal navigation (Cheats Portal trigger,
 * Create-Challenge parent tracking, Daily prompt coordinator slot).
 *
 * GameContainer remains responsible for game state + layout; this component
 * is the single authority for "which modal is visible and how do they
 * hand off to one another".
 */
export function ModalCoordinator({
  // Core game / user
  visitorId,
  imageUrl,
  difficulty,
  time,
  moves,
  scoreData,
  gridSize,
  isComplete,
  isFirstPlay,
  playWithImage,

  // Cheats Portal wiring (cheat codes act on the game engine)
  toggleHint,
  autoSolveWithTargetScore,

  // Secret menu wiring
  autoSolve,
  resetCornerTouch,

  // Completion modal state
  completionModalData,
  clearCompletionModal,
  clearForReplay,

  // Game action handlers
  handleRegularNewPuzzle,
  handleReplayPuzzle,
  handleChallengeNewPuzzle,
  handlePlayChallenge,
  handlePlayDaily,
  handleDailySaveAndShare,
  handleHintConfirm,

  // Hooks
  modals,
  daily,
  challenge,
  hintFeature,
  pushNotifications,
  theme,
  setTheme,
  tapMode,

  // Layout / fullscreen (for daily prompt)
  isMobile,
  fullscreenSupported,
  isFullscreen,
  requestFullscreen,
}) {
  // ── Cheats Portal (7-tap version Easter Egg) ───────────────────────────
  const [showCheatsPortal, setShowCheatsPortal] = useState(false);

  useEffect(() => {
    const openPortal = () => setShowCheatsPortal(true);
    window.addEventListener('cheats-portal-open', openPortal);
    return () => window.removeEventListener('cheats-portal-open', openPortal);
  }, []);

  const handleCheatsPortalSubmit = useCallback((password) => {
    if (password === 'Sh0w') {
      toggleHint();
      return true;
    }
    if (password === 'S0lv3') {
      if (isComplete) return true;
      const multiplier = DIFFICULTIES[difficulty]?.multiplier || 1.0;
      const target = Math.round((600 + Math.random() * 200) * multiplier);
      autoSolveWithTargetScore(target);
      return true;
    }
    if (password === 'Imag3Bump') {
      // Manually re-pull the image set so users can force a refresh when
      // the weekly rotation hasn't taken effect on their session yet.
      const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
      const t = toast.loading('Bumping image cache...');
      axios
        .post(`${API}/images/cheats-bump`)
        .then((res) => {
          const { puzzle_images_count, daily_images_count } = res.data || {};
          toast.success(
            `Images refreshed - ${puzzle_images_count ?? 0} puzzle, ${daily_images_count ?? 0} daily`,
            { id: t }
          );
        })
        .catch(() => {
          toast.error('Image bump failed', { id: t });
        });
      return true;
    }
    return false;
  }, [toggleHint, isComplete, difficulty, autoSolveWithTargetScore]);

  // ── Create-Challenge parent tracking ───────────────────────────────────
  // Tracks which modal (if any) the CreateChallenge modal was opened from,
  // so that closing CreateChallenge returns the user to that modal instead
  // of all the way back to the Home screen.
  const [createChallengeParent, setCreateChallengeParent] = useState(null);

  const handleOpenCreateChallengeFromDaily = useCallback(() => {
    setCreateChallengeParent('daily');
    modals.openCreateChallenge();
  }, [modals]);

  const handleCloseCreateChallenge = useCallback(() => {
    modals.closeCreateChallenge();
    if (createChallengeParent === 'daily') {
      modals.openDailyChallenge();
    }
    setCreateChallengeParent(null);
  }, [modals, createChallengeParent]);

  // Called when the user takes a forward action from inside CreateChallenge
  // ("New Puzzle", "Play Challenge", "Replay"). In that case we should NOT
  // re-open the parent modal — the user is explicitly moving on.
  const clearCreateChallengeParent = useCallback(() => {
    setCreateChallengeParent(null);
  }, []);

  // ── Secret menu handlers (coordinate with cornerTouch reset) ───────────
  const handleSecretShowHint = useCallback(() => {
    toggleHint();
    resetCornerTouch();
  }, [toggleHint, resetCornerTouch]);

  const handleSecretAutoComplete = useCallback(() => {
    if (!isComplete) autoSolve();
    resetCornerTouch();
  }, [isComplete, autoSolve, resetCornerTouch]);

  const handleSecretMenuClose = useCallback(() => {
    modals.closeSecretMenu();
    resetCornerTouch();
  }, [modals, resetCornerTouch]);

  // ── Daily prompt effect ────────────────────────────────────────────────
  // Route through the top-level modal coordinator so it never stacks on top
  // of the PWA install prompt or update banner, and benefits from a subtle
  // handoff delay so users don't get two popups at once.
  useEffect(() => {
    if (!daily.shouldShowDailyPrompt(
      challenge.pendingChallengeCode,
      challenge.showChallengeInfo,
      challenge.isChallengeMode
    )) {
      return;
    }

    let cancelWait;
    const initial = setTimeout(() => {
      cancelWait = topModalCoordinator.waitUntilFree(() => {
        if (challenge.pendingChallengeCode || challenge.showChallengeInfo || challenge.isChallengeMode) return;
        topModalCoordinator.open('daily');
        modals.openDailyPrompt();
        daily.markDailyPromptShown();
      });
    }, 1500);

    return () => {
      clearTimeout(initial);
      cancelWait?.();
    };
  }, [challenge.pendingChallengeCode, challenge.showChallengeInfo, challenge.isChallengeMode, daily, modals]);

  // Release the 'daily' slot in the coordinator when the daily prompt closes.
  useEffect(() => {
    if (!modals.showDailyPrompt) topModalCoordinator.close('daily');
  }, [modals.showDailyPrompt]);

  return (
    <>
      <CompletionModal
        isOpen={!!completionModalData && !completionModalData.isDailyMode && !completionModalData.isChallengeMode}
        onClose={() => {}}
        scoreData={completionModalData?.scoreData}
        time={completionModalData?.time}
        moves={completionModalData?.moves}
        difficulty={completionModalData?.difficulty}
        imageUrl={completionModalData?.imageUrl}
        deviceId={visitorId}
        onNewPuzzle={handleRegularNewPuzzle}
        onReplayPuzzle={handleReplayPuzzle}
        gridSize={completionModalData?.gridSize}
        hintPenalty={completionModalData?.hintPenalty || 0}
        isFirstPlay={isFirstPlay}
      />

      <ChallengeCompletionModal
        isOpen={!!completionModalData && completionModalData.isChallengeMode}
        onClose={() => {}}
        scoreData={completionModalData?.scoreData}
        time={completionModalData?.time}
        moves={completionModalData?.moves}
        challengeCode={completionModalData?.challengeCode}
        onNewPuzzle={handleChallengeNewPuzzle}
        onReplayPuzzle={handleReplayPuzzle}
        deviceId={visitorId}
      />

      <DailyCompletionModal
        isOpen={!!completionModalData && completionModalData.isDailyMode}
        onClose={() => {}}
        scoreData={completionModalData?.scoreData}
        time={completionModalData?.time}
        moves={completionModalData?.moves}
        puzzleDate={completionModalData?.dailyPuzzleDate}
        deviceId={visitorId}
        onSaveAndShare={handleDailySaveAndShare}
        onReplayPuzzle={handleReplayPuzzle}
        onNewPuzzle={handleRegularNewPuzzle}
      />

      <CreateChallengeModal
        isOpen={modals.showCreateChallenge}
        onClose={handleCloseCreateChallenge}
        imageUrl={completionModalData?.imageUrl || imageUrl}
        difficulty={completionModalData?.difficulty || difficulty}
        playerName={localStorage.getItem('jigsaw_daily_player_name')}
        scoreData={completionModalData?.scoreData || scoreData}
        time={completionModalData?.time || time}
        moves={completionModalData?.moves || moves}
        deviceId={visitorId}
        onPlayChallenge={(...args) => { clearCreateChallengeParent(); handlePlayChallenge(...args); }}
        pushNotifications={pushNotifications}
        onReplayPuzzle={() => { clearCreateChallengeParent(); handleReplayPuzzle(); }}
        onNewPuzzle={() => { clearCreateChallengeParent(); handleRegularNewPuzzle(); }}
      />

      <AvailableChallengesModal
        isOpen={modals.showAvailableChallenges}
        onClose={modals.closeAvailableChallenges}
        onPlayChallenge={handlePlayChallenge}
        deviceId={visitorId}
      />

      <ChallengeInfoModal
        isOpen={challenge.showChallengeInfo}
        onClose={challenge.closeChallengeInfo}
        challengeCode={challenge.pendingChallengeCode}
        onPlay={handlePlayChallenge}
      />

      <SettingsModal
        isOpen={modals.showSettings}
        onClose={modals.closeSettings}
        theme={theme}
        onThemeChange={setTheme}
        useTapMode={tapMode.useTapMode}
        onTapModeChange={tapMode.toggleTapMode}
      />

      <HelpModal
        isOpen={modals.showHelp}
        onClose={modals.closeHelp}
      />

      <LeaderboardModal
        isOpen={modals.showLeaderboard}
        onClose={modals.closeLeaderboard}
        onPlayImage={playWithImage}
      />

      <HistoryModal
        isOpen={modals.showHistory}
        onClose={modals.closeHistory}
        deviceId={visitorId}
        onPlayImage={playWithImage}
      />

      <DailyChallengeModal
        isOpen={modals.showDailyChallenge}
        onClose={modals.closeDailyChallenge}
        onPlay={handlePlayDaily}
        hasPlayedToday={daily.hasPlayedDailyToday}
        todayScore={daily.todayDailyScore}
        playerStreak={daily.playerStreak}
        onChallengeFriend={handleOpenCreateChallengeFromDaily}
        deviceId={visitorId}
      />

      {/* Daily Challenge Prompt */}
      <AlertDialog open={modals.showDailyPrompt} onOpenChange={modals.setShowDailyPrompt}>
        <AlertDialogContent className="relative">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => modals.setShowDailyPrompt(false)}
            className="absolute top-2 right-2 h-8 w-8"
            data-testid="daily-prompt-close-btn"
          >
            <X className="h-4 w-4" />
          </Button>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <span className="text-2xl">🏆</span>
              Daily Challenge Available!
            </AlertDialogTitle>
            <AlertDialogDescription>
              Today's puzzle is ready! Compete with players worldwide on the same puzzle. Would you like to try it?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => {
              if (isMobile && fullscreenSupported && !isFullscreen) {
                requestFullscreen();
              }
            }}>Maybe Later</AlertDialogCancel>
            <AlertDialogAction onClick={() => {
              if (isMobile && fullscreenSupported && !isFullscreen) {
                requestFullscreen();
              }
              modals.closeDailyPrompt();
              modals.openDailyChallenge();
            }}>
              Let's Go!
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Hint Confirmation Dialog */}
      <AlertDialog open={hintFeature.showConfirmation} onOpenChange={hintFeature.cancelHint}>
        <AlertDialogContent className="relative">
          <Button
            variant="ghost"
            size="icon"
            onClick={hintFeature.cancelHint}
            className="absolute top-2 right-2 h-8 w-8"
            data-testid="hint-prompt-close-btn"
          >
            <X className="h-4 w-4" />
          </Button>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <span className="text-2xl">💡</span>
              Use a Hint?
            </AlertDialogTitle>
            <AlertDialogDescription>
              A hint will highlight a random unplaced piece and show where it goes on the board for 5 seconds.
              <br /><br />
              <span className="font-semibold text-destructive">Cost: -{hintFeature.hintCost} points</span>
              {hintFeature.hintsUsed > 0 && (
                <span className="block mt-1 text-muted-foreground">
                  Hints used so far: {hintFeature.hintsUsed} (-{hintFeature.hintPenalty} points total)
                </span>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleHintConfirm}>
              Use Hint (-{hintFeature.hintCost} pts)
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <SecretMenuModal
        isOpen={modals.showSecretMenu}
        onClose={handleSecretMenuClose}
        onShowHint={handleSecretShowHint}
        onAutoComplete={handleSecretAutoComplete}
        isComplete={isComplete}
      />

      <CheatsPortalModal
        isOpen={showCheatsPortal}
        onClose={() => setShowCheatsPortal(false)}
        onSubmit={handleCheatsPortalSubmit}
      />
    </>
  );
}
